import type { PaymentReconciliationDetail } from "@medplum/fhirtypes";

import { LEDGER_DETAIL_TYPE, type LedgerDetailType } from "@/billing/constants/ledger-detail-type";
import { Systems } from "@/fhir/enums/systems";

/**
 * Classifies a PaymentReconciliation detail as a posted payment or adjustment. Checks every HL7
 * payment-type coding, so an unrecognized code before a payment or adjustment code does not hide it.
 *
 * @param detail One detail line of a PaymentReconciliation.
 * @returns The detail type, or undefined when the line is neither a payment nor an adjustment.
 */
export function getDetailType(detail: PaymentReconciliationDetail): LedgerDetailType | undefined {
  const code = detail.type?.coding?.find(
    (coding) => coding.system === Systems.HL7PaymentType && isPostedDetailCode(coding.code),
  )?.code;
  return isPostedDetailCode(code) ? code : undefined;
}

/**
 * Tells whether a ledger detail is a payment reversal: a payment detail below zero.
 *
 * @param type The detail type.
 * @param amount The detail amount.
 * @returns True when the detail is a payment with an amount below zero.
 */
export function isPaymentReversalDetail(type: LedgerDetailType, amount: number): boolean {
  return type === LEDGER_DETAIL_TYPE.Payment && amount < 0;
}

function isPostedDetailCode(
  code: string | undefined,
): code is typeof LEDGER_DETAIL_TYPE.Payment | typeof LEDGER_DETAIL_TYPE.Adjustment {
  return code === LEDGER_DETAIL_TYPE.Payment || code === LEDGER_DETAIL_TYPE.Adjustment;
}
